import { isValidCampaignSlug, useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { isMynaviMode } from '@/components/Functional/utils/campaignTypes'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

/**
 * _app.tsx の AppContent から呼び出すグローバルガードフック。
 * マイナビモードで通常ページにアクセスした場合、キャンペーン TOP（`/{slug}`）にリダイレクトする。
 *
 * マイナビモードで許可されるルートパターン:
 * - `/[campaign]` --- TOPページ（MyNaviHome を表示）
 * - `/[campaign]/mynavi/*` --- マイナビ専用サブページ
 *
 * NOTE: asPath（実際のURL）ではなく router.pathname（ルートパターン）を使用する。
 *       asPath はクエリ文字列やハッシュを含む場合があり、完全一致が失敗して
 *       /oc2026 上で無限リダイレクトが起きるため。
 */
export function useRegularPageGuard(): void {
  const router = useRouter()
  const { campaignSlug } = useCampaignSlug()

  useEffect(() => {
    const guard = () => {
      // router が確定するまで（SSG ハイドレーション前）ガードを実行しない。
      // router.isReady が false の間は router.query が未設定のため、useCampaignSlug が
      // env 由来の serviceName を返す場合があり、誤リダイレクトのリスクがある。
      if (!router.isReady) return
      // SSG ハイドレーション前はスラグが確定しないためスキップ
      if (!isValidCampaignSlug(campaignSlug)) return
      // マイナビモード以外は制限なし
      if (!isMynaviMode()) return

      // pathname はルートパターン（例: '/[campaign]', '/[campaign]/spots'）であり
      // クエリ文字列・ハッシュ・末尾スラッシュに依存しないため安全に比較できる
      const { pathname } = router
      const isMynaviAllowed = pathname === '/[campaign]' || pathname.startsWith('/[campaign]/mynavi')

      if (!isMynaviAllowed) {
        router.replace(`/${campaignSlug}`)
      }
    }

    // 初回マウント時チェック（直接 URL 入力・リロード対応）
    guard()

    // クライアントサイドナビゲーション後チェック（_app.tsx の既存パターンと統一）
    router.events.on('routeChangeComplete', guard)
    return () => {
      router.events.off('routeChangeComplete', guard)
    }
  }, [campaignSlug, router])
}
